import React from "react";

import { Edit2, Info } from "lucide-react";

import { Button } from "~/components/ui/button";

interface EditableNotesFieldProps {
  title: string;
  value: string;
  editedValue: string;
  onEditedValueChange: (value: string) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  isSaving: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
  canEdit: boolean;
  emptyText?: string;
  helpText?: string;
  placeholder?: string;
  /** Optional hard cap on the number of characters while editing. */
  maxLength?: number;
}

/**
 * A reusable component for editable text fields in order details
 * Used for internal notes, factura observations, etc.
 */
export const EditableNotesField: React.FC<EditableNotesFieldProps> = ({
  title,
  value,
  editedValue,
  onEditedValueChange,
  isEditing,
  setIsEditing,
  isSaving,
  onSave,
  onCancel,
  canEdit,
  emptyText = "No hay información disponible.",
  helpText,
  placeholder = "Añadir información...",
  maxLength,
}) => {
  const charCount = editedValue.length;
  const atLimit = maxLength != null && charCount >= maxLength;
  const nearLimit = maxLength != null && charCount >= Math.floor(maxLength * 0.9);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
        <h3 className="flex items-center text-lg font-semibold text-gray-800">
          <Edit2 className="mr-2 h-5 w-5 text-gray-600" />
          {title}
        </h3>
        {canEdit && !isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            Editar
          </Button>
        )}
      </div>

      <div className="p-5">
        {!isEditing ? (
          <>
            <p
              className={`whitespace-pre-wrap text-sm ${value ? "text-gray-700" : "text-muted-foreground"}`}
            >
              {value || emptyText}
            </p>
            {helpText && (
              <p className="mt-2 flex items-center text-xs text-muted-foreground">
                <Info className="mr-1 h-4 w-4" />
                {helpText}
              </p>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <textarea
              className="min-h-[100px] w-full rounded-md border border-input p-2 text-sm"
              value={editedValue}
              onChange={(e) => onEditedValueChange(e.target.value)}
              placeholder={placeholder}
              disabled={isSaving}
              maxLength={maxLength}
            />
            {maxLength != null && (
              <p
                className={`text-right text-xs ${
                  atLimit
                    ? "text-red-600"
                    : nearLimit
                      ? "text-amber-600"
                      : "text-muted-foreground"
                }`}
              >
                {charCount} / {maxLength}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button size="sm" onClick={onSave} disabled={isSaving}>
                {isSaving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
